import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { ArrowDownFromLine, Clock, AlertCircle } from "lucide-react";

/**
 * NiftySyncWidget — Dashboard tile that surfaces "when did you last import
 * from Nifty?" as a first-class signal instead of buried tribal knowledge.
 *
 * Since Nifty has no API to pull from, we can't literally auto-sync — but
 * a visible "N hours since last sync" badge does the job by nudging her at
 * a glance every morning. Tone escalates: green (< 24h), amber (< 48h),
 * red (48h+ or never). A one-tap link jumps to /admin/nifty-import.
 */
export function NiftySyncWidget() {
  const { settings } = useSettings();
  if (!settings) return null;

  const invAt = settings.last_nifty_inventory_sync_at;
  const ordAt = settings.last_nifty_orders_sync_at;
  const mostRecent = mostRecentIso(invAt, ordAt);
  const hours = mostRecent ? Math.floor((Date.now() - new Date(mostRecent).getTime()) / 3_600_000) : null;

  const tone = hours == null ? "red" : hours < 24 ? "green" : hours < 48 ? "amber" : "red";
  const toneCls = {
    green: "border-emerald-500/60 bg-emerald-50",
    amber: "border-amber-500/60 bg-amber-50",
    red: "border-red-500/50 bg-red-50",
  }[tone];
  const iconCls = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
  }[tone];

  return (
    <Link
      to="/admin/nifty-import"
      className={`block border p-5 hover:shadow-md transition-shadow ${toneCls}`}
      data-testid="nifty-sync-widget"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="editorial-label flex items-center gap-1.5 text-charcoal">
            <ArrowDownFromLine className={`w-3 h-3 ${iconCls}`} />
            nifty sync
          </p>
          <p className="font-serif text-2xl text-charcoal mt-2" data-testid="nifty-sync-status">
            {hours == null ? "never synced" : hours === 0 ? "synced just now" : `${hours}h ago`}
          </p>
          <p className="text-xs text-taupe mt-1">
            {tone === "red" && (
              <span className="inline-flex items-center gap-1 text-red-800">
                <AlertCircle className="w-3 h-3" /> {hours == null ? "drop nifty's csvs to start" : "orders may be missing from p&l"}
              </span>
            )}
            {tone === "amber" && "consider pulling today's csvs"}
            {tone === "green" && (
              <span className="inline-flex items-center gap-1 text-emerald-800">
                <Clock className="w-3 h-3" /> accounting is current
              </span>
            )}
          </p>
        </div>
      </div>
      {(invAt || ordAt) && (
        <div className="mt-3 pt-3 border-t border-taupe/20 text-[0.6rem] tracking-widest uppercase text-taupe space-y-0.5">
          <p>listings: {invAt ? new Date(invAt).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—"}</p>
          <p>orders: {ordAt ? new Date(ordAt).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—"}</p>
        </div>
      )}
    </Link>
  );
}

function mostRecentIso(a, b) {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
}
